import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Package, Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/641bb6aa-cf02-4790-9e0d-c49c3442ce20/images/3e8727a0ac9b112c3d69005b44aa6e21260d5c413b22216d7a14d79ade4c71e2.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.full_name || u.name || u.email}.`);
      const target =
        u.role === "STORE_DISPATCHER" ? "/dispatcher" :
        u.role === "STORE_OWNER" ? "/store" :
        u.role === "STORE_MANAGER" ? "/store" :
        u.role === "RIDER" ? "/rider" :
        u.role === "WAREHOUSE_ADMIN" ? "/warehouse" :
        u.role === "WAREHOUSE_MANAGER" ? "/warehouse" :
        u.role === "WAREHOUSE_PACKAGE_DISPATCHER" ? "/warehouse" : "/admin";
      navigate(target);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* Visual side */}
      <div className="relative hidden md:block overflow-hidden border-r border-slate-200 dark:border-slate-800">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/40 to-slate-950/90" />
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <Link to="/" data-testid="login-brand-link" className="flex items-center gap-2">
            <Logo className="h-12" />
          </Link>
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-3">
              / SUPER ADMIN CONSOLE
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] max-w-md">
              The control room
              <br />for every package.
            </h2>
            <p className="mt-5 text-slate-600 dark:text-slate-400 max-w-sm">
              One login. Total visibility. Approve operators, monitor inventory, and ship faster.
            </p>
            <div className="mt-10 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-mono-tech">
              <ShieldCheck className="w-4 h-4 text-cyan-400" />
              Encrypted · Role-gated · JWT
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex flex-col items-center justify-center px-6 sm:px-12 py-12 relative">
        <Link
          to="/"
          data-testid="login-back-home"
          className="absolute top-6 left-6 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white transition-colors"
        >
          ← Back to site
        </Link>

        <div className="w-full max-w-md">
          <div className="md:hidden mb-10">
            <Logo className="h-12" />
          </div>

          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-3">
            / SIGN IN
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            Super Admin Login
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">Enter your credentials to access the command center.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Email
              </Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@logistics.com"
                  className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white pl-10 h-11 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Password
              </Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white pl-10 h-11 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
            >
              {loading ? "Signing in…" : (
                <>
                  Sign in <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-900/50 text-xs text-slate-600 dark:text-slate-400">
            New store?{" "}
            <Link to="/register/store-owner" data-testid="login-go-register" className="text-cyan-400 hover:text-cyan-300">
              Register as a store owner →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
